import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAME_RE = /^[A-Za-z][A-Za-z '.\-]{0,49}$/;
const MOBILE_RE = /^[6-9]\d{9}$/;

export default function EntryGateDialog({ open, onSuccess }) {
  const [firstName, setFirstName] = useState("");
  const [mobile, setMobile] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!NAME_RE.test(firstName.trim())) e.firstName = "Enter a valid name (letters only)";
    if (!MOBILE_RE.test(mobile.trim())) e.mobile = "Enter a 10-digit Indian mobile (starts with 6–9)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      const { data } = await axios.post(`${API}/leads/capture`, {
        first_name: firstName.trim(),
        mobile: mobile.trim(),
      });
      onSuccess({ leadId: data.lead_id, firstName: firstName.trim(), mobile: mobile.trim() });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setErrors({
          mobile: "This number is already registered. Please use another number.",
        });
      } else if (!err?.response) {
        // Network error — fail silently and let the visitor use the site
        onSuccess({ leadId: null, firstName: firstName.trim() || "", mobile: "", fallback: true });
      } else {
        const msg =
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Could not continue. Please try again.";
        toast.error(typeof msg === "string" ? msg : "Please check the form.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="bg-[#0A0F1C] border border-white/10 text-white sm:max-w-[460px] p-0 overflow-hidden [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="entry-gate-dialog"
      >
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#F97316]/20 blur-3xl pointer-events-none" />

        <DialogHeader className="relative px-6 sm:px-8 pt-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F97316]">
            <Sparkles className="w-3.5 h-3.5" />
            Welcome to Sahi Disha
          </div>
          <DialogTitle className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-3">
            Your personalised plan is almost ready
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400 mt-2">
            Enter your name and mobile to continue. A Sahi Disha advisor will reach out with tailored guidance.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="relative px-6 sm:px-8 pb-8 pt-6 space-y-4">
          <div>
            <Label htmlFor="gate-name" className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Your name
            </Label>
            <Input
              id="gate-name"
              autoFocus
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                if (errors.firstName) setErrors((x) => ({ ...x, firstName: undefined }));
              }}
              placeholder="Rahul"
              className="mt-2 h-12 bg-white/5 border-white/10 text-white focus-visible:border-[#F97316] focus-visible:ring-[#F97316]/30"
              data-testid="entry-gate-name"
              aria-invalid={!!errors.firstName}
            />
            {errors.firstName && (
              <p className="mt-1.5 text-xs text-red-400" data-testid="entry-gate-name-error">
                {errors.firstName}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="gate-mobile" className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Mobile number
            </Label>
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-[#F97316]" />
                +91
              </span>
              <Input
                id="gate-mobile"
                inputMode="numeric"
                maxLength={10}
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                  if (errors.mobile) setErrors((x) => ({ ...x, mobile: undefined }));
                }}
                placeholder="98240 50200"
                className="pl-14 h-12 bg-white/5 border-white/10 text-white num-font text-base focus-visible:border-[#F97316] focus-visible:ring-[#F97316]/30"
                data-testid="entry-gate-mobile"
                aria-invalid={!!errors.mobile}
              />
            </div>
            {errors.mobile && (
              <p className="mt-1.5 text-xs text-red-400" data-testid="entry-gate-mobile-error">
                {errors.mobile}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium shadow-lg shadow-orange-500/20"
            data-testid="entry-gate-submit"
          >
            {loading ? "Please wait…" : "Continue to my plan"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <p className="text-[11px] text-slate-500 text-center">
            We respect your privacy — your details stay with Sahi Disha.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
